/**
 * 统一海报创建 sticky 底部 CTA —— UX-1(2026-05-30)合并 free-create / apply-template 两份
 * SubmitFooter 为一个。整页 fixed 一行版式(取自 FreeCreate)。批次徽章 + 预计耗时文案统一走
 * `freeCreate.*` 命名空间;仅「忙碌/生成」动态文案由各创建流传入。
 */
import { Calendar, ChevronRight, Coins, Crop, Loader2, Sparkles, Wand2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/shared/ui/badge";
import { Button } from "@/shared/ui/button";
import { Input } from "@/shared/ui/input";
import { cn } from "@/shared/lib/utils";

import { PRICE_PENDING_TEXT } from "../../../model/free-create/submitGate";

export interface PosterSubmitFooterProps {
  /** 本次出图张数 — 驱动批次徽章 + 预计耗时区间(1 张 / 多张两档) */
  count: number;
  /**
   * 批次徽章文案。不给则用默认「一次生成 N 版供你挑选」。
   *
   * ★组图 / 套图必须给:那两种模式下 N 张**不是同一张的 N 版**(组图每张讲一个爆点、
   *   套图每张一个比例),说「供你挑选」是错的 —— 用户不是要从里面挑一张,是三张都要。
   */
  batchLabel?: string;
  /** 生成按钮是否可点 */
  canGenerate: boolean;
  /** 忙碌(提交 / 润色)— 禁按钮 + 转圈 */
  busy: boolean;
  /** 忙碌时按钮文案(如「生成中…」「AI 润色中…」) */
  busyLabel: string;
  /** 空闲时按钮文案(如「生成海报」) */
  generateLabel: string;
  /** 方案 A — 预计扣积分(= 后端实扣,本批 tier×张数);未知(capabilities 未到货)时不显示。 */
  estimatedCredits?: number;
  /**
   * 0926:报价依赖的能力表拉不到 → 显示「价格待系统核算」而不是什么都不显示
   * (什么都不显示 + 按钮还能点 = 用户不知道要花多少钱)。
   */
  priceUnavailable?: boolean;
  /** 按钮为什么点不动(如「正在保存你涂选的区域…」)。★禁用原因必须**贴着按钮**说:
   *  原本这句写在页面下方,用户涂完伸手点按钮,只看到按钮变灰、看不见原因(真机量到
   *  提示在 y≈1038、按钮在固定底栏 y≈944 —— 根本不在同一屏)。未传/可点时不显示。 */
  disabledReason?: string | null;
  /** 本次生效画幅(如 "9:16" / "智能" / "跟随原图 · 3:4")。常驻显示,连续创作时用户一眼可见,
   *  避免比例悄悄退回默认(2026-07-17 生产实锤)。未传则不显示。 */
  ratioLabel?: string;
  /** T1:窄屏底栏的「画风 · 配色 · 叠加」一行摘要(宽屏由右回显列承担,这里 hidden)。 */
  styleLabel?: string;
  /** 点摘要滚回样式段 —— 看得见还要够得着。 */
  onJumpToStyle?: () => void;
  onGenerate: () => void;
  /**
   * 次要动作(B2-1 组图分步:主按钮「先出封面」,这里给「一次全出」)。
   * ★分步是默认,但必须给得起钱、赶时间的人一条直路 —— 只留一个按钮就成了强制。
   */
  secondaryAction?: { label: string; onClick: () => void } | null;
  scheduleMode: "now" | "later";
  setScheduleMode: (m: "now" | "later") => void;
  scheduledAt: string | null;
  setScheduledAt: (v: string) => void;
}

export function PosterSubmitFooter({
  count,
  batchLabel,
  canGenerate,
  busy,
  busyLabel,
  generateLabel,
  estimatedCredits,
  priceUnavailable,
  ratioLabel,
  styleLabel,
  onJumpToStyle,
  disabledReason,
  onGenerate,
  secondaryAction,
  scheduleMode,
  setScheduleMode,
  scheduledAt,
  setScheduledAt,
}: PosterSubmitFooterProps) {
  const { t } = useTranslation("poster");
  return (
    /* ★2026-07-31 `fixed left-0` → `sticky`:原来底栏从视口最左开始,而应用侧栏
       (实测 <nav> x=0 w=74,不透明且层级在上)把最左那个 chip 压掉半截 ——
       页面上看到的是「成 1 版供你挑选」,真值是「一次生成 1 版供你挑选」。
       ★为什么不用 CSS 变量对齐:实测这个侧栏**不是 shadcn 的 Sidebar**(普通 <nav>、
         无 data-state/data-collapsible),而挂在外层的 `--sidebar-width:16rem` /
         `--sidebar-width-icon:3rem` 与它实际的 74px 对不上 —— 按变量写会是个"看着对、
         量出来错"的假修。
       改 sticky 后底栏跟着内容流走,左边界天然 = 内容区左边界,侧栏收展都不用管。
       `-mx-4 md:-mx-6` 抵消 <main> 的 p-4/p-6,让它仍横满内容区而不侵入侧栏。 */
    <div className="sticky bottom-0 z-20 -mx-4 border-t border-border/60 bg-background/80 backdrop-blur md:-mx-6">
      <div className="flex flex-wrap items-center justify-between gap-3 p-3">
        <div className="flex flex-wrap items-center gap-3">
          <Badge variant="outline" className="gap-1 border-primary/30 text-primary">
            <Sparkles className="h-3 w-3" />
            {batchLabel ?? t("freeCreate.submitBatch", { n: count })}
          </Badge>
          {ratioLabel && (
            <Badge
              variant="outline"
              className="gap-1 border-border/60 text-muted-foreground tabular-nums"
            >
              <Crop className="h-3 w-3" />
              {t("freeCreate.submitRatio", { r: ratioLabel })}
            </Badge>
          )}
          {/* T1:窄屏(<1040px)右回显列会塌到页面最底,等于没有回显 —— 这一行是最后一眼。
              点它滚回样式段(而不是只显示),否则看见了也改不动。 */}
          {styleLabel && (
            <button
              type="button"
              onClick={onJumpToStyle}
              className="max-w-[42vw] truncate text-[12.5px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline min-[1040px]:hidden"
              title="点这里回到「画成什么样」"
            >
              {styleLabel}
            </button>
          )}
          <span className="text-[12.5px] text-muted-foreground">
            {/* ★1004 T2:原来写「预计 ~{count×3}s」—— 演示机实测单张要 1–2 分钟,差两个数量级。
                写实际区间;多张不承诺上限(并发与排队都会拉长)。 */}
            {t("freeCreate.submitEta", {
              range: count > 1 ? t("freeCreate.etaBatch") : t("freeCreate.etaSingle"),
            })}
          </span>
          {priceUnavailable && (
            <span
              data-price-pending
              className="flex items-center gap-1 text-[12.5px] font-medium text-muted-foreground"
            >
              <Coins className="h-3 w-3" />
              {PRICE_PENDING_TEXT}
            </span>
          )}
          {!priceUnavailable && estimatedCredits != null && (
            <span className="flex items-center gap-1 text-[12.5px] font-medium text-amber-600 tabular-nums">
              <Coins className="h-3 w-3" />
              {t("freeCreate.submitCredits", { c: estimatedCredits })}
            </span>
          )}
          {/* ★2026-07-31 两个独立描边按钮 → **段控**。
              原来「立即生成 / 定时投放」各带一圈 border、选中的还染主色,长得和右边真正的
              提交按钮「生成海报」是同一个物种 —— 底栏上并排三个"看着能点的东西",
              用户分不出哪个才是出图。它其实只是「什么时候发」的二选一。
              形制照抄本页已有的段控(FreeCreateBody 的 modeSeg:共享一个 muted 槽 +
              选中项浮起)—— 同一页里同一种"二选一"就该长同一个样。 */}
          {/* ★1004 T2:段控两项原叫「立即生成 / 定时投放」—— 「立即生成」读起来就是提交按钮,
              用户点了它等出图。改成带前缀标签的「出图时间:马上出图 / 定时出图」,一眼看出是个选项。 */}
          <div className="ml-2 flex items-center gap-1.5" data-testid="poster-schedule-toggle">
            <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-[12.5px] text-muted-foreground">
              {t("freeCreate.scheduleLabel")}
            </span>
            <div
              className="inline-flex shrink-0 rounded-lg bg-muted/60 p-0.5"
              role="tablist"
              aria-label="什么时候出图"
            >
              {(["now", "later"] as const).map((mode) => {
                const active = scheduleMode === mode;
                const label =
                  mode === "now" ? t("freeCreate.scheduleNow") : t("freeCreate.scheduleLater");
                return (
                  <button
                    key={mode}
                    type="button"
                    role="tab"
                    onClick={() => setScheduleMode(mode)}
                    className={cn(
                      "rounded-[7px] px-2.5 py-1 text-[12.5px] font-medium transition-colors cursor-pointer",
                      active
                        ? "bg-background text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground"
                    )}
                    /* role=tab 的选中态是 aria-selected;aria-pressed 是 button 的,
                       两个一起写在 role=tab 上不合法(eslint jsx-a11y 报 error)——
                       存量问题,合 main 后闸门红在这里,顺手去掉多余那个 */
                    aria-selected={active}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
            {scheduleMode === "later" && (
              <Input
                type="datetime-local"
                value={scheduledAt ?? ""}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="h-7 max-w-[180px] text-[12.5px]"
              />
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!busy && !canGenerate && disabledReason && (
            <span className="max-w-[280px] text-right text-[12.5px] leading-snug text-muted-foreground">
              {disabledReason}
            </span>
          )}
          {/* B2-1:次要动作(组图分步时的「一次全出」)。放主按钮左边、弱化样式 ——
              它是给赶时间的人的直路,不是默认路。 */}
          {secondaryAction && (
            <Button
              size="lg"
              variant="outline"
              disabled={!canGenerate || busy}
              onClick={secondaryAction.onClick}
              className="h-11 cursor-pointer px-4 text-[13px] font-medium disabled:cursor-not-allowed disabled:opacity-50"
            >
              {secondaryAction.label}
            </Button>
          )}
          <Button
            size="lg"
            disabled={!canGenerate || busy}
            onClick={onGenerate}
            className="h-11 cursor-pointer gap-2 px-6 font-semibold disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            {busy ? busyLabel : generateLabel}
            {!busy && <ChevronRight className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
